import { Button, Dropdown, DropdownIcon, Input, Loader, Modal } from "@trafficsharkdsp/ui";
import cl from "classnames";
import { useEffect, useMemo, useRef, useState, type ComponentProps, type ReactNode } from "react";

import cls from "./CreateOrCopyEntity.module.css";

import EmptyIcon from "@/shared/assets/icons/emptyPageIcon.svg";
import SearchIcon from "@/shared/assets/icons/searchIcon.svg";

export interface CreateOrCopyOption {
    key: string;
    label: ReactNode;
    searchText: string;
}

export type CreateOrCopyEntityPosition = ComponentProps<typeof Dropdown>["position"];

interface CreateOrCopyEntityProps<TOption extends CreateOrCopyOption> {
    children: ReactNode;
    copyLabel: string;
    createLabel: string;
    description: string;
    emptyText: string;
    errorText: string;
    isError?: boolean;
    isLoading?: boolean;
    modalTitle: string;
    onCopy: (option: TOption) => Promise<void> | void;
    onCopyModalOpenChange?: (isOpen: boolean) => void;
    onCreate: () => void;
    options: TOption[];
    searchPlaceholder: string;
    selectPlaceholder: string;
    cancelLabel: string;
    confirmLabel: string;
    dropdownWidth?: string;
    position?: CreateOrCopyEntityPosition;
    triggerClassName?: string;
    qaTags?: {
        button?: string;
        copy?: string;
        create?: string;
        /** Prefix for the copy modal elements: `<prefix>-modal`, `<prefix>-select`, `<prefix>-search-input`… */
        copyModal?: string;
    };
}

export const CreateOrCopyEntity = <TOption extends CreateOrCopyOption>({
    cancelLabel,
    children,
    confirmLabel,
    copyLabel,
    createLabel,
    description,
    dropdownWidth,
    emptyText,
    errorText,
    isError = false,
    isLoading = false,
    modalTitle,
    onCopy,
    onCopyModalOpenChange,
    onCreate,
    options,
    position = "rightBottom",
    searchPlaceholder,
    selectPlaceholder,
    triggerClassName,
    qaTags,
}: CreateOrCopyEntityProps<TOption>) => {
    const [isMenuOpen, setIsMenuOpen] = useState(false);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [isSelectorOpen, setIsSelectorOpen] = useState(false);
    const [isCopying, setIsCopying] = useState(false);
    const [search, setSearch] = useState("");
    const [selectedKey, setSelectedKey] = useState<string | null>(null);
    const selectorRef = useRef<HTMLDivElement>(null);

    const copyQa = (element: string) =>
        qaTags?.copyModal ? `${qaTags.copyModal}-${element}` : undefined;
    const selectedOption = options.find((option) => option.key === selectedKey) ?? null;
    const filteredOptions = useMemo(() => {
        const query = search.trim().toLowerCase();
        if (!query) return options;
        return options.filter((option) => option.searchText.toLowerCase().includes(query));
    }, [options, search]);

    useEffect(() => {
        if (!isSelectorOpen) return;

        const closeOnOutsideClick = (event: MouseEvent) => {
            if (!selectorRef.current?.contains(event.target as Node)) {
                setIsSelectorOpen(false);
            }
        };

        document.addEventListener("mousedown", closeOnOutsideClick);
        return () => document.removeEventListener("mousedown", closeOnOutsideClick);
    }, [isSelectorOpen]);

    const resetCopyState = () => {
        setIsSelectorOpen(false);
        setSearch("");
        setSelectedKey(null);
        setIsCopying(false);
    };

    const closeModal = () => {
        setIsModalOpen(false);
        resetCopyState();
        onCopyModalOpenChange?.(false);
    };

    const openModal = () => {
        setIsMenuOpen(false);
        resetCopyState();
        setIsModalOpen(true);
        onCopyModalOpenChange?.(true);
    };

    const createEntity = () => {
        setIsMenuOpen(false);
        onCreate();
    };

    const copyEntity = async () => {
        if (!selectedOption || isCopying) return;

        setIsCopying(true);
        try {
            await onCopy(selectedOption);
            closeModal();
        } finally {
            setIsCopying(false);
        }
    };

    return (
        <>
            <Modal
                destroyOnClose
                footer={
                    <div className={cls.footer}>
                        <Button
                            htmlType="button"
                            onClick={closeModal}
                            type="secondary"
                            buttonProps={{ "data-qa": copyQa("cancel-button") }}
                        >
                            {cancelLabel}
                        </Button>
                        <Button
                            disabled={!selectedOption || isLoading || isError || isCopying}
                            htmlType="button"
                            isLoading={isCopying}
                            onClick={copyEntity}
                            type="primary"
                            buttonProps={{ "data-qa": copyQa("continue-button") }}
                        >
                            {confirmLabel}
                        </Button>
                    </div>
                }
                isOpen={isModalOpen}
                onClose={closeModal}
                data-qa={copyQa("modal")}
            >
                <div className={cls.content}>
                    <h2 className={cls.title}>{modalTitle}</h2>
                    <span className={cls.description}>{description}</span>

                    <div className={cls.selector} ref={selectorRef}>
                        <button
                            aria-expanded={isSelectorOpen}
                            className={cl(cls["selector-trigger"], isSelectorOpen && cls.open)}
                            disabled={isLoading || isError}
                            onClick={() => setIsSelectorOpen((isOpen) => !isOpen)}
                            type="button"
                            data-qa={copyQa("select")}
                        >
                            <span
                                className={cl(
                                    cls["selected-value"],
                                    !selectedOption && cls.placeholder,
                                )}
                            >
                                {selectedOption?.label ?? selectPlaceholder}
                            </span>
                            <DropdownIcon className={cls["dropdown-icon"]} />
                        </button>

                        {isSelectorOpen && (
                            <div className={cls.dropdown} role="listbox">
                                <Input
                                    autoFocus
                                    onChange={(event) => setSearch(event.target.value)}
                                    placeholder={searchPlaceholder}
                                    prefixIcon={<SearchIcon />}
                                    value={search}
                                    data-qa={copyQa("search-input")}
                                />
                                <div className={cls.list}>
                                    {filteredOptions.length > 0 ? (
                                        filteredOptions.map((option) => (
                                            <button
                                                aria-selected={option.key === selectedKey}
                                                className={cl(
                                                    cls.option,
                                                    option.key === selectedKey && cls.selected,
                                                )}
                                                key={option.key}
                                                onClick={() => {
                                                    setSelectedKey(option.key);
                                                    setIsSelectorOpen(false);
                                                    setSearch("");
                                                }}
                                                role="option"
                                                type="button"
                                                data-qa={copyQa("list-option")}
                                            >
                                                {option.label}
                                            </button>
                                        ))
                                    ) : (
                                        <div className={cls.empty}>
                                            <EmptyIcon />
                                            <span>{emptyText}</span>
                                        </div>
                                    )}
                                </div>
                            </div>
                        )}
                    </div>

                    {isLoading && (
                        <div className={cls.status}>
                            <Loader />
                        </div>
                    )}
                    {isError && <div className={cls.error}>{errorText}</div>}
                </div>
            </Modal>

            <Dropdown
                btnClassName={triggerClassName}
                isOpen={isMenuOpen}
                items={[
                    {
                        key: "create",
                        label: createLabel,
                        onClick: createEntity,
                        itemProps: { "data-qa": qaTags?.create },
                    },
                    {
                        key: "copy",
                        label: copyLabel,
                        onClick: openModal,
                        itemProps: { "data-qa": qaTags?.copy },
                    },
                ]}
                onListClick={() => setIsMenuOpen(false)}
                onToggle={setIsMenuOpen}
                position={position}
                width={dropdownWidth}
                data-qa={qaTags?.button}
            >
                {children}
            </Dropdown>
        </>
    );
};
